import React, { useState, useEffect, useMemo, useRef } from 'react';

const US_TIMEZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Indiana/Indianapolis',
  'America/Kentucky/Louisville',
  'America/Chicago',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Adak',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
];

const ALL_TIMEZONES = (() => {
  const supported = typeof Intl.supportedValuesOf === 'function'
    ? new Set(Intl.supportedValuesOf('timeZone'))
    : null;
  return US_TIMEZONES.filter((tz) => !supported || supported.has(tz));
})();

export { ALL_TIMEZONES };

export default function TimeZoneCombobox({ value, onChange, className = '' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const displayValue = value ? value.replace(/_/g, ' ') : '';

  const options = useMemo(() => {
    if (value && !ALL_TIMEZONES.includes(value)) return [value, ...ALL_TIMEZONES];
    return ALL_TIMEZONES;
  }, [value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase().replace(/\s+/g, '_');
    return options.filter((tz) => tz.toLowerCase().includes(q));
  }, [query, options]);

  useEffect(() => {
    function handleMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  function handleSelect(tz) {
    onChange(tz);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          placeholder={value ? displayValue : 'Search US timezones…'}
          value={query || (open ? query : displayValue)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 focus:border-[#4f7a64] pr-8 text-sm"
          autoComplete="off"
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </div>
      {open && (
        <ul className="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg text-sm">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-gray-400">No timezones found</li>
          ) : (
            filtered.map((tz) => (
              <li
                key={tz}
                onMouseDown={() => handleSelect(tz)}
                className={`px-3 py-2 cursor-pointer hover:bg-[#4f7a64]/10 ${tz === value ? 'bg-[#4f7a64]/10 font-medium text-[#4f7a64]' : 'text-gray-700'}`}
              >
                {tz.replace(/_/g, ' ')}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
